import { Stack, Typography } from "@mui/material";
import { useLangContext } from "../../../../../context/langContext";
import { useFilesContext } from "../../../../../context/myFilesContext";
import compInfos from "./file-description.json";
import { dateFormatter } from "../../../../../utils/date-formatter";
import InfoBadge, { OriginEnum } from "../../../../common/info-badge/InfoBadge";
import { LoadingButton } from "@mui/lab";
import MuiBaseModal from "../../../../common/modal/MuiModal";
import AudioPlayer from "../../../../common/audio-player/AudioPlayer";
import MuiTextField from "../../../../common/text-field/MuiTextField";
import { useState } from "react";
import { useNotificationContext } from "../../../../../context/notificationContext";
import errorResolver from "../../../../../utils/error-resolver";
import { FileVerifyRequest } from "../../../../../services/Endpoints/files/FileVerifyEndpoint";
import { useProtectedAxiosInstance } from "../../../../../hooks/axiosInstance";
import { FileRejectRequest } from "../../../../../services/Endpoints/files/FileRejectEndpoint";
import { useCanAccess } from "../../../../../hooks/canAccess";
import { AccessEnums } from "../../../../../constants/accessEnum";

export default function FileDescriptionModal() {
  const { currentFile, setCurrentFile, fileReload, setFileReload } =
    useFilesContext();
  const { lang, dir } = useLangContext();
  const [description, setDescription] = useState("");
  const { setNotification } = useNotificationContext();
  const protectedInstance = useProtectedAxiosInstance();
  const [waiting, setWaiting] = useState(false);

  const handleDescChange = (e: any) => {
    setDescription(e.target.value);
  };
  const infos = compInfos[lang];

  const handleReload = () => setFileReload(!fileReload);
  const { can } = useCanAccess();

  const handleVerify = async () => {
    const fileVerifyRequest = new FileVerifyRequest(protectedInstance);
    try {
      setWaiting(true);
      const resp = await fileVerifyRequest.send(Number(currentFile?.id ?? 1));
      setNotification({ type: "success", message: resp.message });
      setCurrentFile(null);
      setWaiting(false);
      handleReload();
      setDescription("");
    } catch (error) {
      console.error(error);
      setWaiting(false);
      errorResolver({ error, setNotification });
    }
  };

  const handleReject = async () => {
    if (!description) {
      setNotification({
        type: "error",
        message: "توضیحات برای رد فایل ضروری است.",
      });
      return;
    }
    const fileRejectRequest = new FileRejectRequest(protectedInstance);
    try {
      setWaiting(true);
      const resp = await fileRejectRequest.send(Number(currentFile?.id ?? 1), {
        description,
      });

      setNotification({ type: "success", message: resp.message });
      setCurrentFile(null);
      setDescription("");
      setWaiting(false);
      handleReload();
    } catch (error) {
      console.error(error);
      errorResolver({ error, setNotification });
      setWaiting(false);
    }
  };

  return (
    <MuiBaseModal
      open={!!currentFile}
      onClose={() => setCurrentFile(null)}
      title={infos.title}
      hasCloseIcon
    >
      {currentFile && (
        <Stack direction="column" gap={"20px"}>
          <Stack
            direction="row"
            alignItems="start"
            justifyContent="space-around"
            gap="25px"
            sx={{ pt: "20px" }}
          >
            <Stack direction="column">
              <Typography
                variant="labelRegular"
                color={(theme) => theme.palette.dark[20]}
                mb="6px"
              >
                {infos.modalSections.keys.created_at}
              </Typography>
              <Typography
                variant="warningRegular"
                color={(theme) => theme.palette.dark[40]}
              >
                {dateFormatter({ lang, date: currentFile!.created_at })}
              </Typography>
            </Stack>
            <Stack direction="column">
              <Typography
                variant="labelRegular"
                color={(theme) => theme.palette.dark[20]}
                mb="6px"
                sx={{
                  display: "flex",
                  justifyContent: "start",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                {infos.modalSections.keys.verifying_status}
                <InfoBadge
                  origin={OriginEnum.FILE}
                  status={currentFile!.verifying_status as "pending"}
                />
              </Typography>
              <Typography
                variant="warningRegular"
                color={(theme) => theme.palette.dark[40]}
              >
                {dateFormatter({ lang, date: currentFile!.updated_at })}
              </Typography>
            </Stack>
          </Stack>

          {currentFile.type.split("/")[0] !== "image" ? (
            <Stack
              width="100%"
              justifyContent="center"
              alignItems="center"
              mt="32px"
              gap="10px"
            >
              <Stack width={"100%"} borderRadius={"4px"}>
                <AudioPlayer src={currentFile!.url} />
              </Stack>
              <Stack width={"100%"}>
                <MuiTextField
                  width="100%"
                  label={infos.label}
                  placeholder={infos.placeholder}
                  name={"description"}
                  type="text"
                  multiline={true}
                  value={description}
                  handleChange={handleDescChange}
                />
              </Stack>
              <Stack
                width="60%"
                direction="row"
                justifyContent="space-around"
                alignItems="center"
                gap="12px"
              >
                <LoadingButton
                  loading={waiting}
                  variant="dangerOutlined"
                  onClick={handleReject}
                  disabled={!can(AccessEnums.AdminFilesRejected)}
                  sx={{
                    mt: "10px",
                    px: "16px",
                    width: "100%",
                  }}
                  type="submit"
                >
                  {infos.removeText}
                </LoadingButton>
                <LoadingButton
                  disabled={!can(AccessEnums.AdminFilesVerified)}
                  loading={waiting}
                  onClick={handleVerify}
                  variant="confirm"
                  sx={{
                    mt: "10px",
                    px: "16px",
                    width: "100%",
                  }}
                  type="submit"
                >
                  {infos.confirmText}
                </LoadingButton>
              </Stack>
            </Stack>
          ) : (
            <Stack
              width="100%"
              justifyContent="center"
              alignItems="center"
              mt="32px"
              gap="10px"
            >
              <Stack width={"100%"} borderRadius={"4px"} height={"300px"}>
                <img src={currentFile.url} width="100%" height="100%" />
              </Stack>

              <Stack
                width="60%"
                direction="row"
                justifyContent="center"
                alignItems="center"
                gap="12px"
              >
                <LoadingButton
                  onClick={() => setCurrentFile(null)}
                  variant="confirm"
                  sx={{
                    mt: "10px",
                    px: "16px",
                    width: "100%",
                  }}
                  type="submit"
                >
                  بستن
                </LoadingButton>
              </Stack>
            </Stack>
          )}
        </Stack>
      )}
    </MuiBaseModal>
  );
}
